import { AuthedUser } from './user'
import { Subscription } from './firestore'
import { PUBLIC_URL } from '../constants/strings'
import { planNumToPlanId } from './plans'
import { fb } from './firebase'
const XSOLLA_URL = process.env.REACT_APP_XSOLLA_URL!
const XSOLLA_PROJECT_ID = process.env.REACT_APP_XSOLLA_PROJECT_ID!
const XSOLLA_SANDBOX = process.env.REACT_APP_XSOLLA_SANDBOX! === 'true'

declare const XPayStationWidget: any

interface OrderConfig {
  order: number
  enable: boolean
}

const getVersion = (mobile: boolean) => {
  return mobile ? 'mobile' : 'desktop'
}

export interface SubscriptionTokenRequest {
  user: {
    id: { value: string; hidden: boolean }
    email: { value: string }
    name: { value: string; hidden: boolean }
  }
  settings: {
    return_url: string
    project_id: number
    currency: string
    language: string
    ui: {
      size?: string
      mode?: string
      desktop: {
        subscription_list: {
          description?: string
          layout?: string
        }
      }
      version: 'mobile' | 'desktop'
      user_account?: {
        subscriptions: OrderConfig
        history: OrderConfig
        payment_accounts: OrderConfig
        info: OrderConfig
      }
      components?: {
        virtual_items: {
          hidden: boolean
        }
        virtual_currency: {
          hidden: boolean
        }
      }
    }
    mode?: string
  }
  purchase?: {
    subscription: {
      plan_id: string
    }
  }
}

export interface SubscriptionPlan {
  charge: {
    period: {
      value: number
      type: string
    }
    prices: {
      amount: number
      currency: string
    }[]
  }
  description: { [key: string]: string }
  name: { [key: string]: string }
  external_id: string
  trial: {
    value: number
    type: string
  }
}

export interface SubscriptionListRecord {
  id: number
  status: string
  date_create: string
  date_end?: string
  date_last_charge?: string
  date_next_charge?: string
  plan_id?: number
}

export interface SubscriptionPlanTokenResponse {
  token: string
}

function subscriptionListRecordTofirestoreRecord(
  listRecord: SubscriptionListRecord
): Subscription {
  const planId = planNumToPlanId.get(listRecord.plan_id!)
  return {
    date_create: listRecord.date_create,
    date_last_charge: listRecord.date_last_charge,
    date_next_charge: listRecord.date_next_charge,
    date_end: listRecord.date_end,
    plan_id: planId ?? '',
  }
}

class XsollaClient {
  payStationLoaded = false
  constructor() {
    console.assert(XSOLLA_URL, 'XSOLLA_URL undefined')
    console.assert(XSOLLA_PROJECT_ID, 'xsolla project id undefined')
  }

  async fetch(url: string, body?: {}) {
    const token = await fb.currentAuthUser()!.getIdToken(true)
    const headers = new Headers()
    headers.set('Authorization', 'Bearer ' + token)
    const method = body ? 'POST' : 'GET'
    if (method === 'POST') {
      headers.set('Accept', 'application/json')
      headers.set('Content-Type', 'application/json')
    }
    const req = await fetch(url, {
      method,
      headers,
      body: JSON.stringify(body),
    })
    return await req.json()
  }

  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    // TODO CAN REQUEST USING CLIENT API
    //return await this.fetch(
    //  `${XSOLLA_URL}/projects/${XSOLLA_PROJECT_ID}/subscriptions/plans?limit=10`
    //)
    throw new Error('not implemented')
  }

  async getCurrentUserSubscriptions(): Promise<Subscription[]> {
    const subs = await this.fetch(`${XSOLLA_URL}/xsollaSubscriptions`)
    return subs.map(subscriptionListRecordTofirestoreRecord)
  }

  async getManageSubscriptionsToken(
    user: AuthedUser,
    mobile: boolean
  ): Promise<SubscriptionPlanTokenResponse> {
    const body: SubscriptionTokenRequest = {
      user: {
        id: { value: user.uid(), hidden: true },
        email: { value: user.email() },
        name: { value: user.displayName(), hidden: false },
      },
      settings: {
        return_url: PUBLIC_URL,
        project_id: parseInt(XSOLLA_PROJECT_ID),
        currency: 'USD',
        language: 'en',
        ui: {
          size: 'medium',
          desktop: {
            subscription_list: {
              layout: 'list',
            },
          },
          version: getVersion(mobile),
          mode: 'user_account',
          user_account: {
            subscriptions: {
              enable: true,
              order: 1,
            },
            payment_accounts: {
              enable: true,
              order: 2,
            },
            history: {
              enable: true,
              order: 3,
            },
            info: {
              enable: true,
              order: 4,
            },
          },
        },
      },
    }
    return await this.fetch(`${XSOLLA_URL}/xsollaToken`, body)
  }

  async getSubscriptionPlanToken(
    user: AuthedUser,
    planId: string,
    mobile: boolean
  ): Promise<SubscriptionPlanTokenResponse> {
    const body: SubscriptionTokenRequest = {
      user: {
        id: { value: user.uid(), hidden: true },
        email: { value: user.email() },
        name: { value: user.displayName(), hidden: false },
      },
      settings: {
        return_url: PUBLIC_URL,
        project_id: parseInt(XSOLLA_PROJECT_ID),
        currency: 'USD',
        language: 'en',
        ui: {
          size: 'medium',
          desktop: {
            subscription_list: {
              layout: 'list',
            },
          },
          version: getVersion(mobile),
          components: {
            virtual_items: {
              hidden: true,
            },
            virtual_currency: {
              hidden: true,
            },
          },
        },
      },
    }

    return await this.fetch(`${XSOLLA_URL}/xsollaToken`, body)
  }

  async renderPayStation(token: string) {
    if (!this.payStationLoaded) {
      await this.loadPayStation()
    }
    XPayStationWidget.init({
      access_token: token,
      sandbox: XSOLLA_SANDBOX,
    })
    XPayStationWidget.open()
  }

  onPayStationStatusDone(callback: (event: any, data: any) => void) {
    XPayStationWidget.on(XPayStationWidget.eventTypes.STATUS_DONE, callback)
  }

  onPayStationClose(callback: (event: any, data: any) => void) {
    XPayStationWidget.on(XPayStationWidget.eventTypes.CLOSE, callback)
  }

  loadPayStation() {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.type = 'text/javascript'
      s.src = '//cdn.xsolla.net/embed/paystation/1.2.7/widget.min.js'
      s.addEventListener('load', resolve, false)
      const head = document.getElementsByTagName('head')[0]
      head.appendChild(s)
      this.payStationLoaded = true
    })
  }
}

export const xsolla = new XsollaClient()
